import { api } from "./api";
import type {
  LessonContent,
  LessonDetail,
  LessonSummary,
  Subject,
} from "@/types/lesson";

const demoSubjects: Subject[] = [
  { id: "math", name: "FNAT", icon: "➗", color: "#f59e0b" },
  { id: "science", name: "Science", icon: "🧪", color: "#10b981" },
  { id: "reading", name: "Reading", icon: "📚", color: "#3b82f6" },
];

const demoLessons: LessonSummary[] = [
  {
    lessonId: "math-1",
    title: "Number Adventure",
    subject: "Math",
    grade: 3,
    thumbnail: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=900&q=80",
    duration: 12,
    completed: false,
    progress: 35,
  },
  {
    lessonId: "science-1",
    title: "Plant Explorer",
    subject: "Science",
    grade: 3,
    thumbnail: "https://images.unsplash.com/photo-1471193945509-9ad0617afabf?auto=format&fit=crop&w=900&q=80",
    duration: 14,
    completed: true,
    progress: 100,
  },
  {
    lessonId: "reading-1",
    title: "Story Time",
    subject: "Reading",
    grade: 3,
    thumbnail: "https://images.unsplash.com/photo-1516979187457-637abb4f9353?auto=format&fit=crop&w=900&q=80",
    duration: 9,
    completed: false,
    progress: 15,
  },
];

const demoLessonDetail: LessonDetail = {
  ...demoLessons[0],
  description: "A friendly lesson to practice adding and subtracting numbers.",
  version: "demo-1",
};

function normalizeArray<T>(value: unknown, fallback: T[]): T[] {
  if (Array.isArray(value)) return value as T[];

  if (value && typeof value === "object") {
    const candidate = value as Record<string, unknown>;
    if (Array.isArray(candidate.items)) return candidate.items as T[];
    if (Array.isArray(candidate.data)) return candidate.data as T[];
    if (Array.isArray(candidate.subjects)) return candidate.subjects as T[];
    if (Array.isArray(candidate.lessons)) return candidate.lessons as T[];
  }

  return fallback;
}

export const lessonApi = {
  getSubjects: async (): Promise<Subject[]> => {
    try {
      const { data } = await api.get<Subject[] | { subjects?: Subject[] }>(
        "/api/subjects",
      );
      return normalizeArray<Subject>(data, demoSubjects);
    } catch {
      return demoSubjects;
    }
  },
  getLessons: async (subjectId?: string): Promise<LessonSummary[]> => {
    try {
      const { data } = await api.get<LessonSummary[] | { lessons?: LessonSummary[] }>(
        "/api/lessons",
        { params: { subjectId } },
      );
      const filteredLessons = normalizeArray<LessonSummary>(data, demoLessons);
      return subjectId
        ? filteredLessons.filter((lesson) => lesson.subject === subjectId)
        : filteredLessons;
    } catch {
      return demoLessons.filter((lesson) =>
        !subjectId || lesson.subject.toLowerCase() === subjectId.toLowerCase(),
      );
    }
  },
  getLesson: async (lessonId: string): Promise<LessonDetail> => {
    if (lessonId === "fnat") {
      return {
        lessonId: "fnat",
        title: "FNAT Lesson",
        subject: "Math",
        grade: 3,
        thumbnail: "",
        duration: 15,
        completed: false,
        progress: 0,
        description: "FNAT Lesson",
        version: "1.0",
      };
    }
    try {
      const { data } = await api.get<LessonDetail>(`/api/lessons/${lessonId}`);
      return data ?? demoLessonDetail;
    } catch {
      return demoLessons.find((lesson) => lesson.lessonId === lessonId)
        ? {
          ...demoLessons.find((lesson) => lesson.lessonId === lessonId)!,
          description: "Demo lesson content is shown because no backend is connected.",
          version: "demo-1",
        }
        : demoLessonDetail;
    }
  },
  /** Backend verifies the student's access and returns a short-lived signed CDN URL. */
  getLessonContent: async (lessonId: string): Promise<LessonContent> => {
    if (lessonId === "fnat") {
      return {
        lessonId,
        swfUrl: "https://voice.camlytix.ai/admin/public/swf/start_final.swf",
        expiresAt: Date.now() + 60 * 60 * 1000,
      };
    }
    try {
      const { data } = await api.get<LessonContent>(
        `/api/lessons/${lessonId}/content`,
      );
      return data ?? {
        lessonId,
        swfUrl: "/lessons/demo.swf",
        expiresAt: Date.now() + 60 * 60 * 1000,
      };
    } catch {
      return {
        lessonId,
        swfUrl: "/lessons/demo.swf",
        expiresAt: Date.now() + 60 * 60 * 1000,
      };
    }
  },
};
